/* XNote use-case pages: the hero's sample-note demo (sections/xp-usecase.liquid; Studio Light, docs/design-v3-light.md
   §2.1; notes docs/build/feedback-0929/build-usecases-business.md).
   Polish round 1 (feedback-0929): the first row is already written when the card first paints (never an empty sheet),
   and the demo starts only once the kinetic H1 has landed (one lead motion at a time).
   Once, when the card is in view: the remaining handwritten rows are written left to right behind an ink tip dot → row by
   row, top to bottom, each handwritten row fades (with a 2 px blur) while its typed line fades in on the same baseline
   → what XNote picks out gets the highlighter → the result appears (search: the query types into a search pill first;
   task: a to-do slides out; audio: a linked recording slides out). ≈ 6 s, then it holds its end state; it plays again
   when the card comes back into view after leaving it (QA fixes V15: no visible "Replay", the owner asked for no
   visible controls on hero loops). Paused off screen and in
   hidden tabs. No scan light, glow or pulse (§1.8 kill list); the payoff is a small 1 → 1.02 → 1 swell of the card.
   Reduced motion / Save-Data / no JS: the static card (handwriting + highlighter + result), never hidden content.
   Web Animations only (transform, opacity, clip-path); no layout reads in a loop.
   The note's rows are fractions of the 520 × 340 sheet, shown 30 units down in a 520 × 330 paper (a top band for the
   tag; the sheet's empty bottom is cropped): paper y = OFF + K × sheet y (xp-usecase.css: top 9.0909 %,
   height 103.0303 %).
   Phones (≤ 767 px): the timeline is SCRUBBED by scroll instead of played by the clock. The animations are built paused
   and each frame sets currentTime = p × total, p = clamp((innerHeight − card top) / (0.65 × innerHeight)): the note
   writes as the card rises and the result lands when the card's top is about 35 % down the screen. One passive scroll
   listener + rAF, attached only while the card intersects; scrolling back reverses it. Desktop and tablets keep the
   timed play (replayed on re-entry). Founders' integration marks (data-uc-src) load after the page's load event. */
(() => {
  'use strict';
  const d = document, w = window, root = d.documentElement;
  const rmq = w.matchMedia('(prefers-reduced-motion: reduce)');
  const conn = navigator.connection || {};
  const lite = root.classList.contains('xp-lite') || Boolean(conn.saveData) || /2g$/.test(conn.effectiveType || '');
  const ease = 'cubic-bezier(.16,1,.3,1)';
  const OFF = 30 / 330, K = 340 / 330;
  const phone = w.matchMedia('(max-width: 767px)');
  let loaded = d.readyState === 'complete';
  let loadedAt = loaded ? performance.now() : 0;
  w.addEventListener('load', () => { loaded = true; loadedAt = performance.now(); }, { once: true });

  function marks(el) {
    const go = () => el.querySelectorAll('img[data-uc-src]').forEach((i) => { i.src = i.dataset.ucSrc; i.removeAttribute('data-uc-src'); });
    if (loaded) go(); else w.addEventListener('load', go, { once: true });
  }

  function init(el) {
    marks(el);
    const rows = Array.from(el.querySelectorAll('.xp-uc-note__row'));
    const lines = Array.from(el.querySelectorAll('.xp-uc-note__line'));
    const pen = el.querySelector('.xp-uc-note__pen');
    const hit = el.querySelector('.xp-uc-note__hit');
    const res = el.querySelector('.xp-uc-note__result');
    const search = el.querySelector('.xp-uc-note__search');
    const q = el.querySelector('.xp-uc-note__q');
    const caret = el.querySelector('.xp-uc-note__caret');
    const card = el.querySelector('.xp-uc-note__card');
    let hold = 0;
    const final = () => { el.classList.remove('is-armed'); el.classList.add('is-final'); };
    if (!rows.length || typeof el.animate !== 'function' || !('IntersectionObserver' in w)) return final();
    if (rmq.matches || lite) return final();
    el.classList.add('is-armed');

    const v = (node, k) => parseFloat(getComputedStyle(node).getPropertyValue(k)) || 0;
    let anims = [], visible = false, started = false, scrubbing = false, total = 0, complete = false, away = false;
    const add = (node, frames, delay, duration, easing = ease, fill = 'both') => { if (node) anims.push(node.animate(frames, { delay, duration, easing, fill })); };

    const build = () => {
      anims.forEach((a) => a.cancel());
      anims = [];
      el.classList.remove('is-done');
      complete = false;
      clearTimeout(hold);
      // 1. Write: the first row is already on the page; each further row wipes in over a time proportional to its
      //    width, and the pen dot rides its baseline.
      let t = 0;
      const pk = [];
      const spans = rows.map((r, i) => {
        const x0 = v(r, '--x0'), x1 = v(r, '--x1'), b = v(r, '--b');
        const dur = i === 0 ? 0 : Math.round(420 + 900 * (x1 - x0));
        const s = { start: t, dur, x0, x1, y0: v(r, '--y0'), y1: v(r, '--y1'), b };
        if (i > 0) t += dur + 140;
        return s;
      });
      const writeEnd = Math.max(1, t - 140);
      spans.forEach((s, i) => {
        if (i === 0) return;
        add(rows[i], [{ clipPath: 'inset(-30% 100% -30% 0)' }, { clipPath: 'inset(-30% -2% -30% 0)' }], s.start, s.dur, 'cubic-bezier(.4,0,.6,1)');
        const top = ((OFF + s.b * K) * 100 - 1.5) + '%';
        pk.push({ offset: s.start / writeEnd, left: s.x0 * 100 + '%', top, opacity: 1 }, { offset: (s.start + s.dur) / writeEnd, left: s.x1 * 100 + '%', top, opacity: 1 });
      });
      if (pen && pk.length) {
        pk[0].opacity = 0;
        pk.push({ offset: 1, left: pk[pk.length - 1].left, top: pk[pk.length - 1].top, opacity: 0 });
        add(pen, pk, 0, writeEnd + 200, 'linear');
      }
      // 2. Convert: row by row, top to bottom, each handwritten row fades out as its typed line fades in (no scan light).
      const t1 = writeEnd + 420, scanDur = 1150;
      spans.forEach((s, i) => {
        const at = t1 + scanDur * Math.min(.95, Math.max(.05, (OFF + (s.y0 + s.y1) / 2 * K + 0.2) / 1.24));
        add(rows[i], [{ opacity: 1, filter: 'none' }, { opacity: 0, filter: 'blur(2px)' }], at, 320, 'ease-out');
        if (lines[i]) add(lines[i], [{ opacity: 0, transform: 'translateY(4px)', filter: 'blur(3px)' }, { opacity: 1, transform: 'none', filter: 'none' }], at + 60, 460);
      });
      let t2 = t1 + scanDur + 150;
      // 3. Search: the query types into its pill, then the hit is marked. Task / audio: marked, then the result.
      if (search && q) {
        add(search, [{ opacity: 0, transform: 'translateY(8px) scale(.96)' }, { opacity: 1, transform: 'none' }], t2, 420);
        const n = Math.max(1, q.textContent.trim().length);
        add(q, [{ clipPath: 'inset(0 100% 0 0)' }, { clipPath: 'inset(0 0 0 0)' }], t2 + 260, 90 * n, `steps(${n}, end)`);
        if (caret) add(caret, [{ opacity: 1, transform: `translateX(-${0.6 * n}em)` }, { opacity: 1, transform: 'none' }], t2 + 260, 90 * n, `steps(${n}, end)`);
        t2 += 260 + 90 * n + 260;
      }
      if (hit) add(hit, [{ backgroundSize: '0% 84%' }, { backgroundSize: '100% 84%' }], t2, 720);
      // The payoff beat: as the highlighter lands, the card swells once (1 → 1.02 → 1).
      const tb = t2 + 560;
      // Per-keyframe easing (fast out to the peak at 35 %, slow settle), the effect itself linear.
      const io = 'cubic-bezier(.65,0,.35,1)';
      if (card) add(card, [{ transform: 'scale(1)', easing: ease }, { transform: 'scale(1.02)', offset: .35, easing: io }, { transform: 'scale(1)' }], tb, 600, 'linear', 'none');
      // A search result: the other lines step back, so the hit reads as found
      // (a colour step to the muted grey, not an opacity fade: the stepped-back lines keep ≥ 4.5:1 on the white sheet).
      if (search) lines.forEach((l) => { if (!l.contains(hit)) add(l, [{ color: getComputedStyle(l).color }, { color: 'rgb(90, 95, 105)' }], t2 + 200, 600, 'ease', 'forwards'); });
      if (res) add(res, [{ opacity: 0, transform: 'translateY(-14px) scale(.97)' }, { opacity: 1, transform: 'none' }], t2 + 380, 620);
      const last = anims.reduce((m, a) => Math.max(m, (a.effect.getTiming().delay || 0) + (a.effect.getTiming().duration || 0)), 0);
      total = last;
      if (scrubbing) { anims.forEach((a) => a.pause()); return; }
      const done = anims.find((a) => (a.effect.getTiming().delay || 0) + (a.effect.getTiming().duration || 0) === last);
      // Hold the result for 800 ms, then "Replay" fades in (CSS opacity transition).
      if (done) done.finished.then(() => { complete = true; el.classList.add('is-done'); }).catch(() => {});
      sync();
    };
    // Pause and resume only while the timeline runs: play() on a finished animation would restart it (scrolling back to a
    // finished card must not replay it behind a visible "Replay").
    const sync = () => { if (scrubbing || complete) return; anims.forEach((a) => (visible && !d.hidden ? a.play() : a.pause())); };
    // Let the kinetic H1 land first (one lead motion at a time): wait for its word animations (at most 2.6 s).
    const h1Done = () => {
      const h = d.querySelector('.xp-uc-stage__title[data-kinetic]');
      const list = h && typeof h.getAnimations === 'function' ? h.getAnimations({ subtree: true }) : [];
      const cap = new Promise((r) => setTimeout(r, Math.max(0, 2600 - (performance.now() - loadedAt))));
      return Promise.race([Promise.all(list.map((a) => a.finished.catch(() => {}))), cap]);
    };
    const maybeStart = () => {
      if (started || !visible || !loaded) return;
      started = true;
      h1Done().then(() => setTimeout(build, 120));
    };
    // Phones: scrub. Built at once (paused), so the card shows the state that matches the scroll position from the
    // first paint after the script runs; no Replay (scrolling back replays it).
    let raf = 0, listening = false;
    const tick = () => {
      raf = 0;
      if (!scrubbing) return;
      const top = el.getBoundingClientRect().top, h = w.innerHeight;
      const p = Math.min(1, Math.max(0, (h - top) / (0.65 * h)));
      const t = p * total;
      anims.forEach((a) => { a.currentTime = t; });
      el.classList.toggle('is-done', p >= 1);
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(tick); };
    const listen = (on) => {
      if (on === listening) return;
      listening = on;
      if (on) { w.addEventListener('scroll', onScroll, { passive: true }); w.addEventListener('resize', onScroll, { passive: true }); onScroll(); }
      else { w.removeEventListener('scroll', onScroll); w.removeEventListener('resize', onScroll); }
    };
    const startScrub = () => { scrubbing = true; started = true; build(); onScroll(); };
    // Larger screens: start at 40 % visibility (the card is in the first viewport), after load, once. Once started,
    // any visibility plays and none pauses.
    new IntersectionObserver((es) => es.forEach((e) => {
      // Scrubbing: settle the state for the current position on every enter AND exit, so a fast scroll past the card
      // never leaves it half-converted (above the viewport = the end state, below = the start state).
      if (scrubbing) { listen(e.isIntersecting); tick(); return; }
      if (started) {
        // A finished card that left the screen plays again when it is back (at least 40 % in view).
        if (complete && !e.isIntersecting) away = true;
        else if (complete && away && e.intersectionRatio >= 0.39 && !d.hidden) { away = false; visible = true; build(); return; }
        visible = e.isIntersecting; sync(); return;
      }
      visible = e.isIntersecting && e.intersectionRatio >= 0.39;
      maybeStart();
    }), { threshold: [0, 0.4, 1] }).observe(el);
    if (phone.matches) startScrub();
    else if (!loaded) w.addEventListener('load', () => setTimeout(maybeStart, 0), { once: true });
    d.addEventListener('visibilitychange', sync);
    rmq.addEventListener('change', () => { if (rmq.matches) { listen(false); scrubbing = false; anims.forEach((a) => a.cancel()); anims = []; final(); } });
    // Crossing the phone breakpoint (rotation, resize): rebuild in the other mode.
    phone.addEventListener('change', () => {
      if (el.classList.contains('is-final')) return;
      if (phone.matches) { startScrub(); if (visible) listen(true); }
      else if (scrubbing) { listen(false); scrubbing = false; started = true; build(); visible = true; sync(); }
    });
  }
  d.querySelectorAll('[data-uc-note]').forEach(init);

  /* "Before you buy" (polish round 2, §12.7 height): the two objections are native <details> that render open, so they
     read as open question-and-answer pairs ≥ 768 px and without JS. On phones they start closed (two 56 px rows instead
     of ≈ 15 lines of answers). This runs at DOMContentLoaded (defer), far below the fold, so nothing visible moves. */
  if (phone.matches) d.querySelectorAll('[data-uc-qa] > details[open]').forEach((el) => { el.open = false; });
})();
